import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Info,
  Play,
} from "lucide-react";
import { Card, KpiCard, Badge } from "../UI";
import { LLM_PLATFORMS } from "./constants";
import styles from "../../_css/Simulation.module.css";
import CreateSimulation from "./CreateSimulation";
import DuplicateSimulation from "./DuplicateSimulation";
import ContinueSimulation from "./ContinueSimulation";
import * as simulationSSE from "./SimulationSSE";
import DeletePopUp from "../Modals/DeletePopUp";
import ActivatePopUp from "../Modals/ActivatePopUp";
import EditSimulationModal from "../Modals/EditSimulationModal";
import { notifyError, notifySuccess } from "../../utils/helper";
import {
  updateProject,
  getSimulations,
  deleteSimulation,
  deleteSimulationPermanent,
  activateSimulation,
  editSimulation,
  get_simulation_stats,
  retrySimulation,
  saveSimulation,
} from "../../requests";
import { deleteAuthToken } from "../../utils/helper";
import { removeUserInformation } from "../../_store/reducers/userReducer";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { setContext } from "../../_store/reducers/contextReducer";
import useRole from "../../hooks/getRole";
import { toggleRefresh } from "../../_store/reducers/refreshReducer";
import {
  SKIP_AUDIENCE_SELECTION_STEP,
  buildGenerateQueriesPayload,
} from "./audienceSelection";

const RUN_STATUS_LABELS = {
  0: "Collecting audience data",
  1: "Audience listed",
  2: "Audience selected",
  3: "Persona generated",
  4: "Queries generated",
  5: "Simulation completed",
  6: "Analysis completed",
  7: "Translation completed",
  10: "Simulation saved",
  90: "Simulation running",
  91: "Scheduled",
  [-1]: "Run failed",
};

function getRunStatusLabel(status, row = null) {
  if (row?.audience_generation_failed) return "Audience generation failed";
  if (status == null) return "—";
  return RUN_STATUS_LABELS[status] ?? `Status ${status}`;
}

function getRunStatusColor(status, row = null) {
  if (row?.audience_generation_failed) return "#ef4444";
  if (status === -1) return "#ef4444";
  if (status === 6) return "#22c55e";
  if (status === 10) return "#f59e0b";
  if (status === 90) return "#3b82f6";
  if (status === 91) return "#a855f7";
  return "#6b7280";
}

/** Numeric status for gating + badge: prefer SSE stage over stale list `p.status` from last fetch. */
function getEffectiveRunStatus(p, liveStageBySimulationId) {
  if (p?.latestSimulationId == null) return p?.status;
  const k = String(p.latestSimulationId);
  const ls = liveStageBySimulationId?.[k];
  if (ls != null && Number.isFinite(Number(ls))) return Number(ls);
  return p?.status;
}

/** Live SSE maps keyed by latestSimulationId string. */
function getRunBadgeFromLive(
  p,
  liveStatusBySimulationId,
  liveStageBySimulationId,
) {
  const k = String(p.latestSimulationId);
  const live = liveStatusBySimulationId[k];
  const colorStatus = getEffectiveRunStatus(p, liveStageBySimulationId);
  if (p?.audience_generation_failed) {
    return {
      colorStatus: -1,
      label: getRunStatusLabel(colorStatus, p),
    };
  }
  const label =
    live != null && String(live).trim() !== ""
      ? live
      : getRunStatusLabel(colorStatus, p);
  return { colorStatus, label };
}

function parseCompetitorBrands(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw))
    return raw.map((x) => String(x).trim()).filter(Boolean);
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.map((x) => String(x).trim()).filter(Boolean)
        : [];
    } catch {
      return raw
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function parseLlmPlatforms(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw))
    return raw.map((x) => String(x).trim()).filter(Boolean);
  if (typeof raw === "string") {
    const value = raw.trim();
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((x) => String(x).trim()).filter(Boolean);
      }
      if (typeof parsed === "string") return [parsed.trim()].filter(Boolean);
    } catch {
      return value
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [value];
  }
  return [String(raw).trim()].filter(Boolean);
}

function mapApiSimulationToUi(s) {
  if (!s) return null;
  const proj = s.project ?? {};
  const brand = proj.brand ?? {};
  const funnelStage = s.funnel_stage;
  const funnel = funnelStage && String(funnelStage).trim() ? [funnelStage] : [];
  const competitors = parseCompetitorBrands(s.competitor_brands);
  const llmPlatforms = parseLlmPlatforms(s.llm_platform);
  const queries = Array.isArray(s.user_queries) ? s.user_queries : [];
  const runCount = s._count?.aiSimulationRuns;
  const simulationsStat = runCount != null ? Number(runCount) : queries.length;
  const isActive = !(
    s.is_active === false ||
    s.is_active === 0 ||
    s.isActive === false ||
    s.active === false
  );
  const im = s.interval_minutes != null ? Number(s.interval_minutes) : null;
  let schedule = null;
  if (im != null && Number.isFinite(im) && im >= 60) {
    if (im === 24 * 60) schedule = "Daily";
    else if (im === 7 * 24 * 60) schedule = "Weekly";
    else if (im === 30 * 24 * 60) schedule = "Monthly";
    else schedule = `Every ${im} min`;
  }

  let nextExecutionAt = s.next_execution_at ?? null;
  if (nextExecutionAt) {
    nextExecutionAt = new Date(nextExecutionAt).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  return {
    id: s.id,
    projectId: proj.id ?? null,
    isActive,
    name: s.name ?? "",
    description: s.description ?? "",
    platform: llmPlatforms[0] ?? "",
    llm_platform: llmPlatforms[0] ?? "",
    llm_platforms: llmPlatforms,
    brand: brand.name ?? "",
    brandId: brand.id ?? null,
    market: "",
    country: "",
    category: "",
    funnel,
    competitors,
    simulation_description: s.description ?? "",
    simulations: simulationsStat,
    lastRun: s.updatedAt ?? s.createdAt ?? null,
    status: s.status ?? 10,
    latestSimulationId: s.id,
    selected_audience: s.selected_audience ?? null,
    mentionRate: null,
    sov: null,
    cost: 0,
    _fromSimulationApi: true,
    language: s.language ?? "English",
    schedule,
    nextExecutionAt,
    error_message:
      s.error_message != null && String(s.error_message).trim() !== ""
        ? String(s.error_message).trim()
        : null,
    audience_generation_failed: Boolean(s.audience_generation_failed),
    audience_prompt:
      s.audience_prompt != null && String(s.audience_prompt).trim() !== ""
        ? String(s.audience_prompt).trim()
        : "",
  };
}

function getMentionRateClass(rate) {
  if (rate == null) return "";
  if (rate >= 70) return styles.high;
  if (rate >= 50) return styles.medium;
  return styles.low;
}

function projectToDuplicateData(p) {
  const llm =
    Array.isArray(p.llm_platforms) && p.llm_platforms.length > 0
      ? p.llm_platforms.map((x) => String(x).toLowerCase())
      : p.llm_platform
        ? [String(p.llm_platform).toLowerCase()]
        : ["chatgpt"];
  console.log(p.language);

  return {
    simulationId: p.latestSimulationId ?? p.id,
    simulationName: p.name ?? "",
    simulationDescription: p.simulation_description ?? "",
    funnelStage: p.funnel?.[0],
    competitorBrands: Array.isArray(p.competitors) ? [...p.competitors] : [],
    language: p.language ?? "English",
    llmPlatforms: llm,
    selectedAudience: p.selected_audience ?? null,
    audiencePrompt: p.audience_prompt ?? "",
    audienceGenerationFailed: Boolean(p.audience_generation_failed),
  };
}

function SimulationsListPage({
  projects = [],
  stats = {},
  loading,
  statsLoading,
  liveStatusBySimulationId = {},
  liveStageBySimulationId = {},
  pagination = null,
  onOpenProject,
  onNewProject,
  onDeactivateSimulation,
  onActivateSimulation,
  onDeleteSimulationPermanent,
  onEditProject,
  onPageChange,
  onEditSimulation,
  onDuplicate,
  onContinue,
  onRetry,
  retryingId = null,
  continuingId = null,
}) {
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const { totalSimulations = 0, totalRuns = 0, averageMentionRate = 0 } = stats;
  const hasPrev = pagination?.hasPrevPage ?? false;
  const hasNext = pagination?.hasNextPage ?? false;
  const currentPage = pagination?.currentPage ?? 1;
  const totalPages = pagination?.totalPages ?? 1;
  const total = pagination?.total ?? 0;
  const perPage = pagination?.perPage ?? 10;

  const handleEditSimulation = (simulation) => {
    onEditProject?.(simulation);
    onEditSimulation?.(simulation);
  };

  const closeDropdown = () => {
    setOpenDropdownId(null);
    setDropdownAnchor(null);
  };

  return (
    <>
      {/* <Header userType={role} headerTitle="Simulations" /> */}
      <div className={styles.page}>
        <div className={styles.headerRow}>
          <div className={styles.titleBlock}>
            <h1>Simulations</h1>
            <p>Create and manage AI simulation projects</p>
          </div>
          {
            <button
              type="button"
              className={styles.newBtn}
              onClick={onNewProject}
            >
              + New Simulation
            </button>
          }
        </div>

        <div className={styles.kpiGrid}>
          <KpiCard
            label="Total Simulations"
            value={statsLoading ? "—" : totalSimulations}
            sub="In this project"
            icon="◈"
          />
          <KpiCard
            label="Total simulation runs"
            value={statsLoading ? "—" : totalRuns}
            sub="All simulation runs"
            icon="⚡"
          />
          <KpiCard
            label="Average mention rate"
            value={
              statsLoading ? "—" : `${Number(averageMentionRate).toFixed(2)}%`
            }
            sub="Client mention rate"
            icon="📊"
          />
        </div>

        {loading ? (
          <div
            className={styles.projectList}
            style={{ textAlign: "center", padding: "20px 0", color: "#999999" }}
          >
            Loading simulations…
          </div>
        ) : projects.length === 0 ? (
          <div
            className={styles.projectList}
            style={{
              textAlign: "center",
              padding: "48px 20px",
              color: "#9ca3af",
              fontSize: 15,
            }}
          >
            No simulations found. Please create a new simulation.
          </div>
        ) : (
          <div className={styles.projectList}>
            {projects.map((p) => {
              const runBadge = getRunBadgeFromLive(
                p,
                liveStatusBySimulationId,
                liveStageBySimulationId,
              );
              const effectiveStatus = getEffectiveRunStatus(
                p,
                liveStageBySimulationId,
              );
              const audienceFailed = Boolean(p.audience_generation_failed);
              const showContinue = effectiveStatus === 1 && !audienceFailed;
              return (
                <div
                  key={p.id}
                  className={styles.projectCard}
                  onClick={() => onOpenProject?.(p)}
                >
                  <Card>
                    <div className={styles.cardInner}>
                      <div className={styles.cardLeft}>
                        <div className={styles.cardHeader}>
                          <span className={styles.cardTitle}>{p.name}</span>
                          <Badge color={p.isActive ? "#22c55e" : "#94a3b8"}>
                            {p.isActive ? "Active" : "Inactive"}
                          </Badge>
                          <Badge
                            color={getRunStatusColor(runBadge.colorStatus, p)}
                          >
                            {runBadge.label}
                          </Badge>
                          {audienceFailed && (
                            <button
                              type="button"
                              className={styles.continueBtn}
                              onClick={(e) => {
                                e.stopPropagation();
                                onDuplicate?.(p);
                              }}
                            >
                              Retry
                            </button>
                          )}
                          {showContinue && (
                            <button
                              type="button"
                              className={styles.continueBtn}
                              disabled={continuingId === p.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onContinue?.(p);
                              }}
                            >
                              <Play
                                size={11}
                                fill="currentColor"
                                strokeWidth={0}
                                aria-hidden
                              />
                              {continuingId === p.id ? "Starting…" : "Continue"}
                            </button>
                          )}
                          {p.status === -1 && (
                            <span
                              className={styles.errorInfoWrap}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className={styles.errorInfoBtn}
                                aria-describedby={`sim-error-tip-${p.id}`}
                                aria-label="Simulation error details"
                              >
                                <Info size={14} strokeWidth={2.5} aria-hidden />
                              </button>
                              <span
                                id={`sim-error-tip-${p.id}`}
                                className={styles.errorInfoTooltip}
                                role="tooltip"
                              >
                                {p.error_message || "Simulation run failed."}
                              </span>
                            </span>
                          )}
                        </div>
                        <div className={styles.cardMeta}>
                          <span>🏢 {p.brand || "—"}</span>
                          {p.market ? <span>🌍 {p.market}</span> : null}
                          {(() => {
                            const platformIds =
                              Array.isArray(p.llm_platforms) && p.llm_platforms.length > 0
                                ? p.llm_platforms
                                : p.llm_platform
                                  ? [p.llm_platform]
                                  : [];
                            if (platformIds.length === 0) return null;
                            const labels = platformIds.map(
                              (id) =>
                                LLM_PLATFORMS.find(
                                  (pl) => pl.id === String(id).toLowerCase(),
                                )?.label ?? id,
                            );
                            return <span>🤖 {labels.join(", ")}</span>;
                          })()}
                          {(p.funnel || []).length > 0 && (
                            <span>🎯 {(p.funnel || []).join(", ")}</span>
                          )}
                          {p.schedule ? (
                            <>
                              <span title="Recurring run frequency">
                                📅 Schedule: {p.schedule}
                              </span>
                              <span title="Next scheduled run">
                                ⏰ Next run: {p.nextExecutionAt ?? "—"}
                              </span>
                            </>
                          ) : null}
                        </div>
                        {(p.competitors || []).length > 0 && (
                          <div className={styles.cardCompetitors}>
                            {(p.competitors || []).map((c) => (
                              <Badge key={c} color="#6b7a90">
                                {c}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={styles.cardRight}>
                        {p.mentionRate !== null && (
                          <div className={styles.statBlock}>
                            <div
                              className={`${styles.statValue} ${getMentionRateClass(p.mentionRate)}`}
                            >
                              {p.mentionRate}%
                            </div>
                            <div className={styles.statLabel}>Mention Rate</div>
                          </div>
                        )}
                        {p.sov !== null && (
                          <div className={styles.statBlock}>
                            <div className={styles.statValue}>{p.sov}</div>
                            <div className={styles.statLabel}>SOV Rank</div>
                          </div>
                        )}
                        <div className={styles.statBlock}>
                          <div className={styles.statValue}>
                            {p.simulations}
                          </div>
                          <div className={styles.statLabel}>Runs</div>
                        </div>
                        <div
                          className={styles.cardActions}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className={styles.dropdownWrap}>
                            <button
                              type="button"
                              className={styles.dropdownTrigger}
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                if (openDropdownId === p.id) {
                                  closeDropdown();
                                } else {
                                  setOpenDropdownId(p.id);
                                  setDropdownAnchor(rect);
                                }
                              }}
                              aria-haspopup="menu"
                              aria-expanded={openDropdownId === p.id}
                              aria-label="Actions"
                            >
                              <MoreVertical size={18} />
                            </button>
                            {openDropdownId === p.id &&
                              dropdownAnchor &&
                              ReactDOM.createPortal(
                                <>
                                  <div
                                    className={styles.dropdownBackdrop}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      closeDropdown();
                                    }}
                                    aria-hidden="true"
                                  />
                                  <ul
                                    className={styles.dropdownMenu}
                                    role="menu"
                                    style={{
                                      top: dropdownAnchor.top + 4,
                                      right:
                                        typeof window !== "undefined"
                                          ? window.innerWidth -
                                            dropdownAnchor.right
                                          : 0,
                                    }}
                                  >
                                    {
                                      <li role="none">
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className={styles.dropdownItem}
                                          onClick={() => {
                                            handleEditSimulation(p);
                                            closeDropdown();
                                          }}
                                        >
                                          Edit
                                        </button>
                                      </li>
                                    }
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                          onDuplicate?.(p);
                                          setTimeout(closeDropdown, 0);
                                        }}
                                      >
                                        Duplicate
                                      </button>
                                    </li>
                                    {audienceFailed && (
                                      <li role="none">
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className={styles.dropdownItem}
                                          onClick={() => {
                                            onDuplicate?.(p);
                                            setTimeout(closeDropdown, 0);
                                          }}
                                        >
                                          Retry
                                        </button>
                                      </li>
                                    )}
                                    {effectiveStatus === -1 && (
                                      <li role="none">
                                        <button
                                          type="button"
                                          role="menuitem"
                                          className={styles.dropdownItem}
                                          disabled={retryingId === p.id}
                                          onClick={() => {
                                            onRetry?.(p);
                                            closeDropdown();
                                          }}
                                        >
                                          {retryingId === p.id
                                            ? "Retrying…"
                                            : "Retry"}
                                        </button>
                                      </li>
                                    )}
                                    {
                                      <>
                                        {p.isActive ? (
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={
                                                styles.dropdownItemDanger
                                              }
                                              onClick={() => {
                                                onDeactivateSimulation?.(p);
                                                closeDropdown();
                                              }}
                                            >
                                              Deactivate
                                            </button>
                                          </li>
                                        ) : (
                                          <li role="none">
                                            <button
                                              type="button"
                                              role="menuitem"
                                              className={styles.dropdownItem}
                                              onClick={() => {
                                                onActivateSimulation?.(p);
                                                closeDropdown();
                                              }}
                                            >
                                              Activate
                                            </button>
                                          </li>
                                        )}
                                      </>
                                    }
                                    <li role="none">
                                      <button
                                        type="button"
                                        role="menuitem"
                                        className={styles.dropdownItemDanger}
                                        onClick={() => {
                                          onDeleteSimulationPermanent?.(p);
                                          closeDropdown();
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </li>
                                  </ul>
                                </>,
                                document.body,
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
        {pagination && totalPages > 0 && total > 0 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              Showing {total === 0 ? 0 : (currentPage - 1) * perPage + 1}–
              {Math.min(currentPage * perPage, total)} of {total}
            </span>
            <div className={styles.paginationBtns}>
              <button
                type="button"
                onClick={() =>
                  hasPrev &&
                  onPageChange?.(pagination.prevPage ?? currentPage - 1)
                }
                disabled={!hasPrev}
                className={styles.pageBtn}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() =>
                  hasNext &&
                  onPageChange?.(pagination.nextPage ?? currentPage + 1)
                }
                disabled={!hasNext}
                className={styles.pageBtn}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const Index = ({ onOpenProject, onNewProject }) => {
  const context = useSelector((state) => state.context);
  const userInfo = useSelector((state) => state.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [duplicateData, setDuplicateData] = useState(null);
  const [continueData, setContinueData] = useState(null);
  const [initialTab, setInitialTab] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState({
    totalSimulations: 0,
    totalRuns: 0,
    averageMentionRate: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [projectToDeletePermanent, setProjectToDeletePermanent] = useState(null);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [simulationToEdit, setSimulationToEdit] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [continuingId, setContinuingId] = useState(null);
  const continuingRef = useRef(false); // #3: blocks a second start while one runs
  const [liveStatusBySimulationId, setLiveStatusBySimulationId] = useState(
    () => simulationSSE.getState().liveStatusBySimulationId,
  );
  const [liveStageBySimulationId, setLiveStageBySimulationId] = useState(
    () => simulationSSE.getState().liveStageBySimulationId,
  );
  const [progressBySimulationId, setProgressBySimulationId] = useState(
    () => simulationSSE.getState().progressBySimulationId,
  );

  const PAGE_LIMIT = 10;
  const role = useRole();


  const handlePageChange = (page) => {
    if (page != null) fetchSimulations(page);
  };

  useEffect(() => {
    const state = location.state;
    if (state?.openCreate) setView("create");
    if (state?.openQueries) setInitialTab("queries");
    if (state?.openDownload) setInitialTab("download");
  }, [location.state]);

  //fetch all simulations
  const fetchSimulations = useCallback(
    async (page = 1) => {
      if (!context?.project_id) return;
      setLoading(true);
      try {
        const res = await getSimulations({
          page,
          limit: PAGE_LIMIT,
          project_id: Number(context.project_id),
        });
        if (res?.status < 400) {
          const body = res?.data ?? res;
          const raw = body?.data ?? body;
          const data = Array.isArray(raw) ? raw : [];
          const pag = body?.pagination ?? res?.pagination ?? null;
          const mappedProjects = data.map(mapApiSimulationToUi).filter(Boolean);
          setProjects(mappedProjects);
          setPagination(pag);
        } else {
          setProjects([]);
          setPagination(null);
        }
      } catch (error) {
        console.error("Failed to fetch simulations", error);
        if (error?.response?.status === 401) {
          deleteAuthToken();
          dispatch(removeUserInformation());
          navigate("/login");
          notifyError(
            error?.response?.data?.message ||
              "Session expired. Please login again.",
          );
        } else {
          notifyError(
            error?.response?.data?.message || "Failed to fetch simulations",
          );
        }
        setProjects([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    },
    [context?.project_id, dispatch, navigate],
  );

  const fetchSimulationStats = useCallback(async () => {
    if (!context?.project_id) {
      setStats({ totalSimulations: 0, totalRuns: 0, averageMentionRate: 0 });
      return;
    }
    setStatsLoading(true);
    try {
      const response = await get_simulation_stats({
        project_id: context.project_id,
      });
      if (response?.status < 400) {
        const d = response?.data ?? {};
        setStats({
          totalSimulations: Number(d.totalSimulations) || 0,
          totalRuns: Number(d.totalRuns) || 0,
          averageMentionRate: Number(d.averageMentionRate) || 0,
        });
      }
    } catch (error) {
      notifyError(
        error?.response?.data?.message || "Failed to fetch simulation stats",
      );
    } finally {
      setStatsLoading(false);
    }
  }, [context?.project_id]);

  const debouncedFetchSimulationStats = useMemo(
    () =>
      debounce(() => {
        fetchSimulationStats();
      }, 800),
    [fetchSimulationStats],
  );

  useEffect(() => {
    return () => {
      debouncedFetchSimulationStats.cancel();
    };
  }, [debouncedFetchSimulationStats]);

  useEffect(() => {
    fetchSimulations(1);
    fetchSimulationStats();
  }, [fetchSimulations, fetchSimulationStats]);

  // Subscribe to global SSE updates (stream keeps running across mount/unmount and reload)
  useEffect(() => {
    const unsubscribe = simulationSSE.subscribe(
      ({
        liveStatusBySimulationId: live,
        liveStageBySimulationId: liveStage,
        progressBySimulationId: prog,
      }) => {
        setLiveStatusBySimulationId(live);
        setLiveStageBySimulationId(liveStage);
        setProgressBySimulationId(prog);
      },
    );
    return unsubscribe;
  }, [userInfo?.agencyId]);

  // Connect SSE for in-progress simulations; prune when they leave the page to free HTTP slots
  useEffect(() => {
    const desired = new Set(
      projects
        .filter(
          (p) =>
            p.latestSimulationId != null &&
            !simulationSSE.isTerminalStatus(p.status),
        )
        .map((p) => String(p.latestSimulationId)),
    );
    simulationSSE.pruneStreamsNotIn(desired);

    const opts = {
      onTerminal: () => {},
      onStatsRequest: debouncedFetchSimulationStats,
    };
    projects.forEach((p) => {
      const runId = p.latestSimulationId;
      if (runId != null && !simulationSSE.isTerminalStatus(p.status)) {
        simulationSSE.connect(runId, opts).catch(() => {});
      }
    });
  }, [projects, userInfo?.agencyId, debouncedFetchSimulationStats]);

  useEffect(() => {
    return () => simulationSSE.pruneStreamsNotIn(new Set());
  }, []);

  const handleOpenProject = (row) => {
    if (!row?.isActive) {
      notifyError("Please activate the simulation to view details");
      return;
    }

    const effectiveStatus = getEffectiveRunStatus(row, liveStageBySimulationId);

    // #3 (2026-10-01): a click anywhere on a ready row does what the Continue
    // button does. It used to open the Audience Profile, which only showed
    // "Persona generation in progress...".
    if (row?.audience_generation_failed) {
      setDuplicateData(projectToDuplicateData(row));
      setView("duplicate");
      return;
    }

    if (effectiveStatus === 1) {
      handleContinueSimulation(row);
      return;
    }

    const status_invalid = [2, 3, 0];
    if (status_invalid.includes(effectiveStatus)) {
      notifyError("AI is still processing the data. Please wait for a moment.");
      return;
    }

    if (effectiveStatus === -1) {
      notifyError("Failed simulation. Please create a new simulation again");
      return;
    }

    const projectId = context?.project_id;
    const simulationId = row?.id ?? row?.latestSimulationId ?? null;

    //store simulation id in context store
    const payload = {
      brand_id: context?.brand_id,
      brand_name: context?.brand_name,
      project_id: context?.project_id,
      project_name: context?.project_name,
      simulation_id: simulationId,
      simulation_name: row?.name ?? "",
    };
    dispatch(toggleRefresh());
    dispatch(setContext(payload));

    //agency id only if user is super admin
    if (role === "super_admin") {
      navigate(
        `/simulations/details/${simulationId}/persona?simulation_id=${simulationId}&project_id=${projectId}&brand_id=${context?.brand_id}&agency_id=${userInfo?.agencyId}`,
      );
    } else {
      navigate(
        `/simulations/details/${simulationId}/persona?simulation_id=${simulationId}&project_id=${projectId}&brand_id=${context?.brand_id}`,
      );
    }

    onOpenProject?.(row);
  };

  const handleConfirmDelete = async () => {
    // if (role === 'user') return notifyError('You are not authorized to deactivate simulations')
    if (!projectToDelete?.id) return;
    try {
      const response = await deleteSimulation(projectToDelete.id);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        setProjectToDelete(null);
        notifySuccess(
          response?.data?.message || "Simulation deactivated successfully",
        );

        if (context.simulation_id === projectToDelete.id) {
          const payload = {
            brand_id: context?.brand_id,
            brand_name: context?.brand_name,
            project_id: context?.project_id,
            project_name: context?.project_name,
            simulation_id: null,
            simulation_name: null,
          };
          dispatch(setContext(payload));
        }
        await fetchSimulations(pagination?.currentPage ?? 1);
        await fetchSimulationStats();
      } else {
        notifyError(
          response?.data?.message || "Failed to deactivate simulation",
        );
        setProjectToDelete(null);
      }
    } catch (err) {
      console.error("Failed to deactivate simulation", err);
      notifyError(
        err?.response?.data?.message || "Failed to deactivate simulation",
      );
      setProjectToDelete(null);
    }
  };

  const handleConfirmDeletePermanent = async () => {
    if (!projectToDeletePermanent?.id) return;
    try {
      const response = await deleteSimulationPermanent(
        projectToDeletePermanent.id,
      );
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        setProjectToDeletePermanent(null);
        notifySuccess(
          response?.data?.message || "Simulation permanently deleted",
        );

        if (context.simulation_id === projectToDeletePermanent.id) {
          const payload = {
            brand_id: context?.brand_id,
            brand_name: context?.brand_name,
            project_id: context?.project_id,
            project_name: context?.project_name,
            simulation_id: null,
            simulation_name: null,
          };
          dispatch(setContext(payload));
        }
        await fetchSimulations(pagination?.currentPage ?? 1);
        await fetchSimulationStats();
      } else {
        notifyError(
          response?.data?.message || "Failed to delete simulation",
        );
        setProjectToDeletePermanent(null);
      }
    } catch (err) {
      console.error("Failed to delete simulation", err);
      notifyError(
        err?.response?.data?.message || "Failed to delete simulation",
      );
      setProjectToDeletePermanent(null);
    }
  };

  const handleConfirmActivate = async () => {
    // if (role === 'user') return notifyError('You are not authorized to activate simulations')
    if (!activateTarget?.id) return;
    try {
      const response = await activateSimulation({ id: activateTarget.id });
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(
          response?.data?.message || "Simulation activated successfully",
        );
        setActivateTarget(null);
        await fetchSimulations(pagination?.currentPage ?? 1);
        await fetchSimulationStats();
      } else {
        notifyError(response?.data?.message || "Failed to activate simulation");
        setActivateTarget(null);
      }
    } catch (err) {
      notifyError(
        err?.response?.data?.message || "Failed to activate simulation",
      );
      setActivateTarget(null);
    }
  };

  const handleConfirmEdit = async (payload) => {
    if (!payload?.id) return;
    setEditLoading(true);
    const editPayload = {
      simulation_id: Number(payload.id),
      name: payload.name ?? "",
      desc: payload.description ?? "",
      competitor_brands: Array.isArray(payload.competitors)
        ? payload.competitors
        : [],
      funnel_stage: payload.funnelStage ?? "",
      llm_platform: Array.isArray(payload.llmPlatforms)
        ? payload.llmPlatforms[0]
        : "",
      llm_platforms: Array.isArray(payload.llmPlatforms)
        ? payload.llmPlatforms
        : [],
    };
    try {
      const response = await editSimulation(editPayload);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(
          response?.data?.message || "Simulation updated successfully",
        );
        fetchSimulations(pagination?.currentPage ?? 1);
        fetchSimulationStats();
      }
      setProjectToEdit(null);
    } catch (err) {
      console.error("Failed to update simulation", err);
      notifyError(
        err?.response?.data?.message || "Failed to update simulation",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleContinueSimulation = async (row) => {
    if (!row?.isActive) {
      notifyError("Please activate the simulation to continue");
      return;
    }

    if (SKIP_AUDIENCE_SELECTION_STEP) {
      const simulationId = row?.id ?? row?.latestSimulationId;
      if (simulationId == null) return;
      // #3: while a start is running, further clicks (row or button) do
      // nothing. A ref, because state updates too late for a quick double click.
      if (continuingRef.current) return;
      continuingRef.current = true;
      setContinuingId(simulationId);
      try {
        const res = await saveSimulation(
          buildGenerateQueriesPayload(simulationId, { isNewSimulation: false }),
        );
        if (res?.status < 400) {
          dispatch(toggleRefresh());
          notifySuccess(
            res?.data?.message ?? "Query generation started successfully",
          );
          await fetchSimulations(pagination?.currentPage ?? 1);
          await fetchSimulationStats();
        } else {
          notifyError(
            res?.data?.message ?? "Failed to start query generation",
          );
        }
      } catch (err) {
        notifyError(
          err?.response?.data?.message ?? "Failed to start query generation",
        );
      } finally {
        continuingRef.current = false;
        setContinuingId(null);
      }
      return;
    }

    setContinueData(row);
    setView("continue");
  };

  const handleRetrySimulation = async (row) => {
    const id = row?.id ?? row?.latestSimulationId;
    if (id == null || retryingId != null) return;
    setRetryingId(id);
    try {
      const response = await retrySimulation(id);
      if (response?.status < 400) {
        notifySuccess(
          response?.data?.message || "Simulation retry started successfully",
        );
        await fetchSimulations(pagination?.currentPage ?? 1);
        await fetchSimulationStats();
      } else {
        notifyError(
          response?.data?.message || "Failed to retry simulation",
        );
      }
    } catch (err) {
      console.error("Failed to retry simulation", err);
      notifyError(
        err?.response?.data?.message || "Failed to retry simulation",
      );
    } finally {
      setRetryingId(null);
    }
  };

  const handleSaveAndGo = async () => {
    setView("list");
    await fetchSimulations(1);
    await fetchSimulationStats();
  };

  const handleQueriesGenerationStarted = async () => {
    setView("list");
    setDuplicateData(null);
    setContinueData(null);
    await fetchSimulations(pagination?.currentPage ?? 1);
    await fetchSimulationStats();
  };

  if (view === "create") {
    return (
      <>
        {/* <Header userType={role} headerTitle="Simulations" /> */}
        <div className={styles.page}>
          <CreateSimulation
            onBack={() => {
              setView("list");
              fetchSimulations(1);
              fetchSimulationStats();
            }}
            onSaveAndGo={handleSaveAndGo}
            onQueriesGenerationStarted={handleQueriesGenerationStarted}
          />
        </div>
      </>
    );
  }

  if (view === "duplicate") {
    return (
      <>
        <div className={styles.page}>
          <DuplicateSimulation
            data={duplicateData ?? {}}
            onBack={() => {
              setDuplicateData(null);
              setView("list");
              fetchSimulations(pagination?.currentPage ?? 1);
              fetchSimulationStats();
            }}
            onSaveAndGo={handleSaveAndGo}
            onQueriesGenerationStarted={handleQueriesGenerationStarted}
          />
        </div>
      </>
    );
  }

  if (view === "continue") {
    return (
      <>
        <div className={styles.page}>
          <ContinueSimulation
            simulationId={continueData?.id ?? continueData?.latestSimulationId}
            initialData={continueData ?? {}}
            onBack={() => {
              setContinueData(null);
              setView("list");
              fetchSimulations(pagination?.currentPage ?? 1);
              fetchSimulationStats();
            }}
            onSaveAndGo={handleQueriesGenerationStarted}
          />
        </div>
      </>
    );
  }

  if (!context?.project_id) {
    return <Navigate to="/projects" />;
  }

  return (
    <>
      <SimulationsListPage
        projects={projects}
        stats={stats}
        loading={loading}
        statsLoading={statsLoading}
        pagination={pagination}
        liveStatusBySimulationId={liveStatusBySimulationId}
        liveStageBySimulationId={liveStageBySimulationId}
        onOpenProject={handleOpenProject}
        onNewProject={() => setView("create")}
        onDeactivateSimulation={setProjectToDelete}
        onActivateSimulation={(p) =>
          setActivateTarget({ id: p.id, name: p.name ?? "" })
        }
        onDeleteSimulationPermanent={setProjectToDeletePermanent}
        onEditProject={setProjectToEdit}
        onEditSimulation={setSimulationToEdit}
        onPageChange={handlePageChange}
        onDuplicate={(p) => {
          setDuplicateData(projectToDuplicateData(p));
          setView("duplicate");
        }}
        onContinue={handleContinueSimulation}
        onRetry={handleRetrySimulation}
        retryingId={retryingId}
        continuingId={continuingId}
      />
      {projectToDelete && (
        <DeletePopUp
          title="Deactivate simulation"
          message={`Are you sure you want to deactivate "${projectToDelete.name}"?`}
          onClose={() => setProjectToDelete(null)}
          onDelete={handleConfirmDelete}
        />
      )}
      {activateTarget && (
        <ActivatePopUp
          title="Activate simulation"
          message={`Are you sure you want to activate "${activateTarget.name}"?`}
          onClose={() => setActivateTarget(null)}
          onActivate={handleConfirmActivate}
        />
      )}
      {projectToDeletePermanent && (
        <DeletePopUp
          variant="delete"
          title="Delete simulation"
          message={`This will permanently delete "${projectToDeletePermanent.name}" and all of its runs and outputs. This cannot be undone.`}
          onClose={() => setProjectToDeletePermanent(null)}
          onDelete={handleConfirmDeletePermanent}
        />
      )}
      {projectToEdit && (
        <EditSimulationModal
          simulation={projectToEdit}
          project={projectToEdit}
          onClose={() => setProjectToEdit(null)}
          onSave={handleConfirmEdit}
        />
      )}
    </>
  );
};

export default Index;
